import { ProductPartStatusEnum } from '@/schemas/productPartStatusEnumSchema'
import { ProductWebshopStatusEnum } from '@/schemas/productWebshopStatusEnumSchema'

import type { ProductPartStatusEnumSchema } from '@/schemas/productPartStatusEnumSchema'
import type { ProductWebshopStatusEnumSchema } from '@/schemas/productWebshopStatusEnumSchema'

const PRICE_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_A,
  ProductWebshopStatusEnum.COMPOSABLE_D,
  ProductWebshopStatusEnum.COMPOSABLE_G,
]

const BUY_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [ProductWebshopStatusEnum.COMPOSABLE_A]

const CONTACT_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_B,
  ProductWebshopStatusEnum.COMPOSABLE_C,
  ProductWebshopStatusEnum.COMPOSABLE_D,
  ProductWebshopStatusEnum.COMPOSABLE_E,
]

const SIGN_UP_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_F,
  ProductWebshopStatusEnum.COMPOSABLE_G,
]

const LOGGED_IN_PRICE_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_A,
  ProductWebshopStatusEnum.COMPOSABLE_C,
  ProductWebshopStatusEnum.COMPOSABLE_D,
  ProductWebshopStatusEnum.COMPOSABLE_E,
  ProductWebshopStatusEnum.COMPOSABLE_G,
  ProductWebshopStatusEnum.COMPOSABLE_F,
]

const LOGGED_IN_BUY_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_A,
  ProductWebshopStatusEnum.COMPOSABLE_C,
  ProductWebshopStatusEnum.COMPOSABLE_G,
  ProductWebshopStatusEnum.COMPOSABLE_F,
]

const LOGGED_IN_CONTACT_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = [
  ProductWebshopStatusEnum.COMPOSABLE_B,
  ProductWebshopStatusEnum.COMPOSABLE_D,
  ProductWebshopStatusEnum.COMPOSABLE_E,
]

const LOGGED_IN_SIGN_UP_BUTTON_STATUS_VALUES: readonly ProductWebshopStatusEnumSchema[] = []

const isIncludedStatus = (
  status: ProductWebshopStatusEnumSchema | null | undefined,
  guestValues: readonly ProductWebshopStatusEnumSchema[],
  isLoggedIn: boolean,
  loggedInValues: readonly ProductWebshopStatusEnumSchema[] = []
): boolean => {
  if (status == null) {
    return false
  }

  return isLoggedIn ? loggedInValues.includes(status) : guestValues.includes(status)
}

/**
 * Hook to determine product display properties based on webshop status
 *
 * - ComposableA: Show price and buy button for all customers
 * - ComposableB: Show contact button only
 * - ComposableC: Logged in - show price and buy button; Guest - show contact button
 * - ComposableD: Show price and contact button
 * - ComposableE: Logged in - show price and contact button; Guest - show contact button only
 * - ComposableF: Logged in - show price and buy button; Guest - show contact/sign-up button only
 * - ComposableG: Logged in - show price and buy button; Guest - show price and contact/sign-up button
 * - No: Don't show price, buy button or contact button - default behavior
 * ===================
 * Product part status can overwrite webshop status and hide price and button
 * - O: Hide price and buy button for all customers - Discontinued product
 */

export const getWebShopStatusVisibility = (
  webShopStatus?: ProductWebshopStatusEnumSchema | null,
  productPartStatus?: ProductPartStatusEnumSchema | null,
  isLoggedIn = false
) => {
  const partStatusActive = productPartStatus === ProductPartStatusEnum.O

  const showPriceWebShopStatus =
    webShopStatus != null
      ? isIncludedStatus(webShopStatus, PRICE_STATUS_VALUES, isLoggedIn, LOGGED_IN_PRICE_STATUS_VALUES)
      : true
  const shouldShowPrice = !partStatusActive && showPriceWebShopStatus

  const showButtonWebShopStatus =
    webShopStatus != null
      ? isIncludedStatus(webShopStatus, BUY_BUTTON_STATUS_VALUES, isLoggedIn, LOGGED_IN_BUY_BUTTON_STATUS_VALUES)
      : true
  const shouldShowBuyButton = !partStatusActive && showButtonWebShopStatus

  const showContactButtonWebShopStatus = isIncludedStatus(
    webShopStatus,
    CONTACT_BUTTON_STATUS_VALUES,
    isLoggedIn,
    LOGGED_IN_CONTACT_BUTTON_STATUS_VALUES
  )
  const shouldShowContactButton = partStatusActive || showContactButtonWebShopStatus

  const showSignUpButtonWebShopStatus = isIncludedStatus(
    webShopStatus,
    SIGN_UP_BUTTON_STATUS_VALUES,
    isLoggedIn,
    LOGGED_IN_SIGN_UP_BUTTON_STATUS_VALUES
  )
  const shouldShowSignUpButton = !partStatusActive && showSignUpButtonWebShopStatus

  return {
    shouldShowPrice,
    shouldShowBuyButton,
    shouldShowContactButton,
    shouldShowSignUpButton,
  }
}
